// ---------------------------------------------------------------------------
// YouAreYou (uru) — RegisterPage
//
// Public page rendered at /register and /register/verify.
//
// Three entry points:
//
// 1. /register  (no location.state)
//    Full V2 journey: First + Last + DOB + Phone → SMS link → /register/verify
//    silent chain → KYC redirect.
//
// 2. /register  (location.state.register_step === "registration_form")
//    From ConnectionRequestPage: phone already verified via SMS/pv token.
//    Show First + Last + DOB only. On submit → registerComplete directly.
//
// 3. /register  (location.state.register_step === "dob_confirm")
//    From ConnectionRequestPage: existing GlobalPerson. Show DOB only.
//    On submit → registerComplete directly.
//
// 4. /register/verify?token=  (SMS link callback — entry point 1 only)
//    Detects "reg_v2" in sessionStorage → silent registerVerify + registerComplete
//    → KYC redirect. If no flag (stale link): show error + restart prompt.
//
// API:
//   POST /api/phonehub/number/normalize
//   POST /api/uru/register/start
//   POST /api/uru/register/verify
//   POST /api/uru/register/complete
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import {
  normalizePhone,
  registerStart,
  registerVerify,
  registerComplete,
} from "@/api/endpoints";
import type { RegisterVerifyResponse, RegisterCompleteResponse } from "@/types/api";
import { formatPhoneDisplay } from "@/lib/utils";
import Icon from "@/components/ui/Icon";
import LogoSpinner from "@/components/ui/LogoSpinner";
import BrandMark from "@/components/ui/BrandMark";

// ---------------------------------------------------------------------------
// State machine
// ---------------------------------------------------------------------------

type PageStep =
  | { kind: "form" }
  | { kind: "submitting" }
  | { kind: "waiting" }
  | { kind: "verifying" }
  | { kind: "completing"; token: string }
  | { kind: "already_on_uru" }
  | { kind: "success"; outcome: RegisterCompleteResponse["next_step"]; kyc_url?: string };

// Describes which variant of the form to render based on entry point.
type FormMode =
  | { kind: "full" }                                                           // entry 1
  | { kind: "name_dob"; token: string; phone: string }                        // entry 2
  | { kind: "dob_only"; token: string; phone: string; personName: string };   // entry 3

interface FieldErrors {
  firstName?: string;
  lastName?: string;
  dob?: string;
  phone?: string;
}

// ---------------------------------------------------------------------------
// Validation helpers
// ---------------------------------------------------------------------------

function getMaxDobDate(): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 18);
  return d.toISOString().split("T")[0];
}

function getMinDobDate(): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 120);
  return d.toISOString().split("T")[0];
}

function validateDob(dob: string): string | undefined {
  if (!dob) return "Date of birth is required.";
  const dobDate = new Date(dob);
  if (dobDate > new Date(getMaxDobDate())) return "You must be at least 18 years old.";
  if (dobDate < new Date(getMinDobDate())) return "Please enter a valid date of birth.";
  return undefined;
}

// ---------------------------------------------------------------------------
// KycRedirecting — auto-redirects to MojoVerify after a brief pause
// ---------------------------------------------------------------------------

function KycRedirecting({ kycUrl }: { kycUrl: string }) {
  useEffect(() => {
    const t = setTimeout(() => { window.location.href = kycUrl; }, 3000);
    return () => clearTimeout(t);
  }, [kycUrl]);

  return (
    <div className="rounded-2xl bg-white p-6 shadow-[0px_4px_20px_rgba(26,31,43,0.06)]">
      <div className="flex flex-col items-center text-center">
        <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-primary-50">
          <Icon name="badge" className="text-3xl text-primary-500" />
        </div>
        <h2 className="text-xl font-bold text-text-primary">Trust Builds Trust.</h2>
        <p className="mt-2 text-sm text-text-secondary">
          Show you are verified to the people you want to prove &lsquo;You are You&rsquo;
        </p>
        <p className="mt-4 flex items-center gap-1.5 text-xs text-text-muted">
          <LogoSpinner size="sm" />
          Starting verification…
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// InfoCards — shown below the form in full mode
// ---------------------------------------------------------------------------

const OPTIONAL_FACTORS = [
  "Phone number",
  "Email address",
  "Home address",
  "SSN verification",
  "Driver's licence",
  "Liveness check",
  "OFAC screening",
  "Adverse media",
];

function InfoCards() {
  return (
    <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
      <div className="rounded-2xl bg-white p-4 shadow-[0px_4px_20px_rgba(26,31,43,0.06)]">
        <div className="mb-2 flex h-8 w-8 items-center justify-center rounded-full bg-primary-50">
          <Icon name="lock" className="text-base text-primary-500" />
        </div>
        <p className="text-sm font-semibold text-text-primary">Your data stays private</p>
        <p className="mt-1 text-xs leading-relaxed text-text-secondary">
          Your ID and documents are never shared with anyone. Only the fact that
          you&apos;re verified is visible — nothing else.
        </p>
      </div>

      <div className="rounded-2xl bg-white p-4 shadow-[0px_4px_20px_rgba(26,31,43,0.06)]">
        <div className="mb-2 flex h-8 w-8 items-center justify-center rounded-full bg-green-50">
          <Icon name="verified_user" className="text-base text-verified" />
        </div>
        <p className="text-sm font-semibold text-text-primary">More than just a verified badge</p>
        <p className="mt-1 text-xs leading-relaxed text-text-secondary">
          When someone checks your Trust Link, they see you&apos;re real and
          verified — nothing more, nothing less.
        </p>
      </div>

      <div className="rounded-2xl bg-white p-4 shadow-[0px_4px_20px_rgba(26,31,43,0.06)]">
        <div className="mb-2 flex h-8 w-8 items-center justify-center rounded-full bg-amber-50">
          <Icon name="add_circle" className="text-base text-amber-500" />
        </div>
        <p className="text-sm font-semibold text-text-primary">Add more later</p>
        <p className="mt-1 text-xs text-text-secondary">Optionally unlock in Settings:</p>
        <ul className="mt-1.5 flex flex-wrap gap-1">
          {OPTIONAL_FACTORS.map((f) => (
            <li
              key={f}
              className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] text-text-secondary"
            >
              {f}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// RegisterPage
// ---------------------------------------------------------------------------

export default function RegisterPage() {
  const [searchParams] = useSearchParams();
  const navigate       = useNavigate();
  const location       = useLocation();

  // Location state from ConnectionRequestPage
  const locationState = location.state as {
    token?: string;
    verified_phone?: string;
    register_step?: "dob_confirm" | "registration_form";
    person_name?: string;
    connection_code?: string;
    connection_uuid?: string;
    from?: string;
    last_name?: string;
    requestor_name?: string;
    requestor_face_url?: string | null;
  } | null;

  const connectionCode = locationState?.connection_code ?? undefined;
  const connectionUuid = locationState?.connection_uuid ?? undefined;

  // Derive form mode from location state
  const formMode: FormMode = (() => {
    const s = locationState;
    if (s?.register_step === "registration_form" && s.token && s.verified_phone) {
      return { kind: "name_dob", token: s.token, phone: s.verified_phone };
    }
    if (s?.register_step === "dob_confirm" && s.token && s.verified_phone) {
      return { kind: "dob_only", token: s.token, phone: s.verified_phone, personName: s.person_name ?? "there" };
    }
    return { kind: "full" };
  })();

  // ---- State ----
  const [step, setStep] = useState<PageStep>({ kind: "form" });
  const [firstName, setFirstName] = useState("");
  const [lastName,  setLastName]  = useState(locationState?.last_name ?? "");
  const [dob,       setDob]       = useState("");
  const [phone,     setPhone]     = useState("");

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [apiError,    setApiError]    = useState<string | null>(null);
  const [verifiedPhone, setVerifiedPhone] = useState(locationState?.verified_phone ?? "");

  const verifyCalledRef = useRef(false);

  const maxDob = getMaxDobDate();
  const minDob = getMinDobDate();

  // ---- On mount: handle ?token= (SMS link callback) ----
  useEffect(() => {
    const token = searchParams.get("token");
    if (!token || verifyCalledRef.current) return;
    verifyCalledRef.current = true;

    setStep({ kind: "verifying" });

    // V2 silent chain: all form data was stored in sessionStorage before registerStart
    const v2Raw = sessionStorage.getItem("reg_v2");
    if (v2Raw) {
      const v2 = JSON.parse(v2Raw) as { firstName: string; lastName: string; dob: string };
      sessionStorage.removeItem("reg_v2");

      registerVerify({ token })
        .then((res: RegisterVerifyResponse) => {
          setVerifiedPhone(res.phone);
          if (res.next_step === "already_on_uru") {
            setStep({ kind: "already_on_uru" });
            return;
          }
          const appUrl = import.meta.env.VITE_APP_URL ?? window.location.origin;
          return registerComplete({
            token,
            date_of_birth: v2.dob,
            ...(res.next_step === "registration_form"
              ? { first_name: v2.firstName, last_name: v2.lastName }
              : {}),
            registration_complete_url: `${appUrl}/auth?registered=true`,
          }).then(dispatchCompleteResponse);
        })
        .catch(() => {
          setApiError("This verification link is invalid or has expired. Please try again.");
          setStep({ kind: "form" });
        });
      return;
    }

    // No sessionStorage flag — stale or unknown link
    setApiError("This verification link has expired or was already used. Please start over.");
    setStep({ kind: "form" });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Shared: dispatch registerComplete response ----
  function dispatchCompleteResponse(res: RegisterCompleteResponse) {
    if (res.next_step === "complete" && res.redirect_url) {
      window.location.href = res.redirect_url;
      return;
    }
    const kycUrl = res.verification_url ?? res.kyc_url;
    setStep({ kind: "success", outcome: kycUrl ? "kyc_started" : res.next_step, kyc_url: kycUrl });
  }

  // ---- Submit ----
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setApiError(null);

      // Validate based on form mode
      const errors: FieldErrors = {};

      if (formMode.kind === "full" || formMode.kind === "name_dob") {
        if (!firstName.trim()) errors.firstName = "First name is required.";
        if (!lastName.trim())  errors.lastName  = "Last name is required.";
      }

      const dobErr = validateDob(dob);
      if (dobErr) errors.dob = dobErr;

      if (formMode.kind === "full") {
        const digits = phone.replace(/\D/g, "");
        if (!phone.trim() || digits.length < 7)
          errors.phone = "Please enter a valid phone number.";
      }

      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        const firstKey = Object.keys(errors)[0] as keyof FieldErrors;
        document.getElementById(`reg-${firstKey}`)?.focus();
        return;
      }
      setFieldErrors({});

      const appUrl = import.meta.env.VITE_APP_URL ?? window.location.origin;

      // --- Connection context: skip SMS, complete directly ---
      if (formMode.kind === "name_dob" || formMode.kind === "dob_only") {
        setStep({ kind: "completing", token: formMode.token });
        try {
          const res = await registerComplete({
            token: formMode.token,
            date_of_birth: dob,
            ...(formMode.kind === "name_dob"
              ? { first_name: firstName.trim(), last_name: lastName.trim() }
              : {}),
            ...(connectionCode ? { connection_code: connectionCode } : {}),
            ...(connectionUuid ? { connection_uuid: connectionUuid } : {}),
            registration_complete_url: `${appUrl}/auth?registered=true`,
          });
          dispatchCompleteResponse(res);
        } catch {
          setApiError(
            formMode.kind === "dob_only"
              ? "Date of birth didn't match our records. Please try again."
              : "Something went wrong completing your registration. Please try again.",
          );
          setStep({ kind: "form" });
          if (formMode.kind === "dob_only") setFieldErrors({ dob: "Date of birth didn't match." });
        }
        return;
      }

      // --- Full flow: normalise phone → registerStart → SMS ---
      setStep({ kind: "submitting" });
      try {
        let normalised: string;
        try {
          const res = await normalizePhone({ phone_number: phone });
          normalised = res.phone_number;
        } catch {
          setFieldErrors({ phone: "Please enter a valid US phone number." });
          setStep({ kind: "form" });
          document.getElementById("reg-phone")?.focus();
          return;
        }

        sessionStorage.setItem(
          "reg_v2",
          JSON.stringify({ firstName: firstName.trim(), lastName: lastName.trim(), dob }),
        );

        await registerStart({ phone: normalised });
        setVerifiedPhone(normalised);
        setStep({ kind: "waiting" });
      } catch {
        sessionStorage.removeItem("reg_v2");
        setApiError("Something went wrong. Please check your number and try again.");
        setStep({ kind: "form" });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formMode, firstName, lastName, dob, phone, connectionCode, connectionUuid],
  );

  // ---- Resend ----
  const handleResend = useCallback(async () => {
    if (!verifiedPhone) return;
    setApiError(null);
    setStep({ kind: "submitting" });
    try {
      await registerStart({ phone: verifiedPhone });
      setStep({ kind: "waiting" });
    } catch {
      setApiError("Unable to resend. Please try again in a moment.");
      setStep({ kind: "waiting" });
    }
  }, [verifiedPhone]);

  const displayPhone = verifiedPhone ? formatPhoneDisplay(verifiedPhone) : phone;
  const isFullMode   = formMode.kind === "full";

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-[#fef0f0] px-4 py-10">
      <div className="w-full max-w-2xl">

        {/* Logo */}
        <div className="mb-6 flex justify-center">
          <BrandMark className="h-20 w-auto" />
        </div>

        {/* Title + subtitle — full mode only, outside card */}
        {isFullMode && step.kind === "form" && (
          <div className="mb-6 text-center">
            <h1 className="text-3xl font-extrabold tracking-tight text-text-primary sm:text-4xl">
              Trust Builds Trust.
            </h1>
            <p className="mt-2 text-base text-text-secondary">
              Verify yourself before asking people to verify themselves.
            </p>
          </div>
        )}

        {/* Spinner states */}
        {(step.kind === "submitting" || step.kind === "verifying") && (
          <div className="rounded-2xl bg-white p-6 shadow-[0px_4px_20px_rgba(26,31,43,0.06)]">
            <div className="flex flex-col items-center py-8">
              <LogoSpinner
                size="md"
                label={step.kind === "verifying" ? "Verifying…" : "Sending verification link…"}
              />
            </div>
          </div>
        )}

        {step.kind === "completing" && (
          <div className="rounded-2xl bg-white p-6 shadow-[0px_4px_20px_rgba(26,31,43,0.06)]">
            <div className="flex flex-col items-center py-8">
              <LogoSpinner size="md" label="Setting up your account…" />
            </div>
          </div>
        )}

        {/* ---- Step 1: Form ---- */}
        {step.kind === "form" && (
          <>
            <div className="overflow-hidden rounded-2xl bg-white px-6 py-6 shadow-[0px_4px_20px_rgba(26,31,43,0.06)]">

              {/* Connection context: requestor banner */}
              {locationState?.requestor_name && (
                <div className="mb-4 flex items-center gap-3 rounded-lg bg-primary-50 px-3 py-2.5">
                  {locationState.requestor_face_url ? (
                    <img
                      src={locationState.requestor_face_url}
                      alt={locationState.requestor_name}
                      className="h-8 w-8 rounded-full object-cover ring-1 ring-primary-200"
                    />
                  ) : (
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-200 text-xs font-bold text-primary-700">
                      {locationState.requestor_name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <p className="text-sm text-primary-700">
                    <span className="font-semibold">{locationState.requestor_name}</span> wants to
                    verify your identity. You&apos;ll be connected after verification.
                  </p>
                </div>
              )}

              {apiError && (
                <div
                  className="mb-4 flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-error"
                  role="alert"
                >
                  <Icon name="error" className="mt-0.5 shrink-0 text-lg" />
                  <p>{apiError}</p>
                </div>
              )}

              <h2 className="mb-5 text-center text-xl font-bold text-text-primary">
                {formMode.kind === "dob_only"
                  ? `Welcome back, ${formMode.personName}!`
                  : "Create Account"}
              </h2>

              {/* Verified phone banner (connection context) */}
              {(formMode.kind === "name_dob" || formMode.kind === "dob_only") && (
                <div className="mb-4 flex items-center gap-2 rounded-lg bg-green-50 px-3 py-2">
                  <Icon name="check_circle" filled className="text-lg text-verified" />
                  <p className="text-sm font-medium text-text-primary">
                    Phone verified:{" "}
                    <span className="text-verified">{formatPhoneDisplay(formMode.phone)}</span>
                  </p>
                </div>
              )}

              <p className="mb-4 text-[11px] font-semibold uppercase tracking-widest text-text-muted">
                {formMode.kind === "dob_only"
                  ? "Confirm your date of birth to continue"
                  : "Let's start by verifying your info"}
              </p>

              <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">

                {/* Name row — full and name_dob modes */}
                {(formMode.kind === "full" || formMode.kind === "name_dob") && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label
                        htmlFor="reg-firstName"
                        className="mb-1 block text-sm font-medium text-text-primary"
                      >
                        First name
                      </label>
                      <input
                        id="reg-firstName"
                        type="text"
                        autoComplete="given-name"
                        autoFocus
                        placeholder="Jane"
                        value={firstName}
                        onChange={(e) => {
                          setFirstName(e.target.value);
                          if (fieldErrors.firstName)
                            setFieldErrors((p) => ({ ...p, firstName: undefined }));
                        }}
                        aria-invalid={!!fieldErrors.firstName}
                        className={`w-full rounded-lg border py-2.5 px-3 text-sm text-text-primary placeholder:text-text-muted focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 focus:outline-none ${
                          fieldErrors.firstName ? "border-error" : "border-gray-300"
                        }`}
                      />
                      {fieldErrors.firstName && (
                        <p className="mt-1 text-xs text-error" role="alert">
                          {fieldErrors.firstName}
                        </p>
                      )}
                    </div>
                    <div>
                      <label
                        htmlFor="reg-lastName"
                        className="mb-1 block text-sm font-medium text-text-primary"
                      >
                        Last name
                      </label>
                      <input
                        id="reg-lastName"
                        type="text"
                        autoComplete="family-name"
                        placeholder="Smith"
                        value={lastName}
                        onChange={(e) => {
                          setLastName(e.target.value);
                          if (fieldErrors.lastName)
                            setFieldErrors((p) => ({ ...p, lastName: undefined }));
                        }}
                        aria-invalid={!!fieldErrors.lastName}
                        className={`w-full rounded-lg border py-2.5 px-3 text-sm text-text-primary placeholder:text-text-muted focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 focus:outline-none ${
                          fieldErrors.lastName ? "border-error" : "border-gray-300"
                        }`}
                      />
                      {fieldErrors.lastName && (
                        <p className="mt-1 text-xs text-error" role="alert">
                          {fieldErrors.lastName}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Date of birth — all modes */}
                <div>
                  <label
                    htmlFor="reg-dob"
                    className="mb-1 block text-sm font-medium text-text-primary"
                  >
                    Date of birth
                  </label>
                  <input
                    id="reg-dob"
                    type="date"
                    autoComplete="bday"
                    autoFocus={formMode.kind === "dob_only"}
                    min={minDob}
                    max={maxDob}
                    value={dob}
                    onChange={(e) => {
                      setDob(e.target.value);
                      if (fieldErrors.dob)
                        setFieldErrors((p) => ({ ...p, dob: undefined }));
                    }}
                    aria-invalid={!!fieldErrors.dob}
                    className={`w-full rounded-lg border py-2.5 px-3 text-sm text-text-primary focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 focus:outline-none ${
                      fieldErrors.dob ? "border-error" : "border-gray-300"
                    } ${!dob ? "text-text-muted" : ""}`}
                  />
                  {fieldErrors.dob ? (
                    <p className="mt-1 text-xs text-error" role="alert">
                      {fieldErrors.dob}
                    </p>
                  ) : (
                    <p className="mt-1 text-xs text-text-muted">
                      You must be at least 18 years old.
                    </p>
                  )}
                </div>

                {/* Phone — full mode only */}
                {formMode.kind === "full" && (
                  <div>
                    <label
                      htmlFor="reg-phone"
                      className="mb-1 block text-sm font-medium text-text-primary"
                    >
                      Phone number
                    </label>
                    <div className="relative">
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <Icon name="phone" className="text-lg text-text-muted" />
                      </div>
                      <input
                        id="reg-phone"
                        type="tel"
                        inputMode="tel"
                        autoComplete="tel"
                        placeholder="(555) 123-4567"
                        value={phone}
                        onChange={(e) => {
                          setPhone(e.target.value);
                          if (fieldErrors.phone)
                            setFieldErrors((p) => ({ ...p, phone: undefined }));
                          if (apiError) setApiError(null);
                        }}
                        aria-invalid={!!fieldErrors.phone}
                        className={`w-full rounded-lg border py-2.5 pl-10 pr-4 text-sm text-text-primary placeholder:text-gray-300 italic focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 focus:outline-none ${
                          fieldErrors.phone ? "border-error" : "border-gray-300"
                        }`}
                      />
                    </div>
                    {fieldErrors.phone ? (
                      <p className="mt-1 text-xs text-error" role="alert">
                        {fieldErrors.phone}
                      </p>
                    ) : (
                      <p className="mt-1 text-xs text-text-muted">
                        We&apos;ll send a verification link to this number.
                      </p>
                    )}
                  </div>
                )}

                <button
                  type="submit"
                  className="mt-1 flex items-center justify-center gap-2 rounded-lg bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600 focus:ring-2 focus:ring-primary-500/20 focus:outline-none"
                >
                  {formMode.kind === "dob_only" ? "Confirm & Continue" : "Start Verification"}
                  <Icon name="arrow_forward" className="text-lg" />
                </button>

                <p className="text-center text-[11px] leading-relaxed text-text-muted">
                  By continuing, you agree to our{" "}
                  <Link
                    to="/terms"
                    className="underline transition-colors hover:text-text-secondary"
                  >
                    Terms of Service
                  </Link>{" "}
                  and{" "}
                  <Link
                    to="/privacy"
                    className="underline transition-colors hover:text-text-secondary"
                  >
                    Privacy Policy
                  </Link>
                  .
                </p>
              </form>
            </div>

            {/* Info cards — full mode only */}
            {isFullMode && <InfoCards />}
          </>
        )}

        {/* ---- Step 2: Waiting for SMS link ---- */}
        {step.kind === "waiting" && (
          <div className="rounded-2xl bg-white p-6 shadow-[0px_4px_20px_rgba(26,31,43,0.06)]">
            <div className="flex flex-col items-center text-center">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-50">
                <Icon name="smartphone" className="text-3xl text-verified" label="Check your phone" />
              </div>
              <h2 className="text-xl font-bold text-text-primary">Check Your Phone</h2>
              <p className="mt-2 text-sm text-text-secondary">
                We&apos;ve sent a verification link to{" "}
                <span className="font-semibold text-text-primary">{displayPhone}</span>.
                Tap it to go straight to identity verification.
              </p>
              <p className="mt-3 text-xs text-text-muted">The link expires in 10 minutes.</p>

              {apiError && (
                <div
                  className="mt-4 flex items-start gap-2 rounded-lg bg-red-50 p-3 text-left text-sm text-error"
                  role="alert"
                >
                  <Icon name="error" className="mt-0.5 shrink-0 text-lg" />
                  <p>{apiError}</p>
                </div>
              )}

              <div className="mt-6 flex w-full flex-col gap-3">
                <button
                  onClick={handleResend}
                  className="text-sm font-medium text-primary-500 transition-colors hover:text-primary-600"
                >
                  Didn&apos;t receive it? Resend
                </button>
                <div className="border-t border-gray-100 pt-3">
                  <button
                    onClick={() => {
                      sessionStorage.removeItem("reg_v2");
                      setStep({ kind: "form" });
                      setApiError(null);
                    }}
                    className="text-sm text-text-muted transition-colors hover:text-text-secondary"
                  >
                    Use a different number
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ---- Already on uru ---- */}
        {step.kind === "already_on_uru" && (
          <div className="rounded-2xl bg-white p-6 shadow-[0px_4px_20px_rgba(26,31,43,0.06)]">
            <div className="flex flex-col items-center text-center">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary-50">
                <Icon name="verified_user" filled className="text-3xl text-primary-500" />
              </div>
              <h2 className="text-xl font-bold text-text-primary">You&apos;re already verified!</h2>
              <p className="mt-2 text-sm text-text-secondary">
                This phone number already has an active account. Sign in to continue.
              </p>
              <button
                onClick={() => navigate("/login")}
                className="mt-6 flex items-center gap-2 rounded-lg bg-primary-500 px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
              >
                Go to Sign In
                <Icon name="arrow_forward" className="text-lg" />
              </button>
            </div>
          </div>
        )}

        {/* ---- Success: KYC redirect ---- */}
        {step.kind === "success" && step.outcome === "kyc_started" && (
          step.kyc_url ? (
            <KycRedirecting kycUrl={step.kyc_url} />
          ) : (
            <div className="rounded-2xl bg-white p-6 shadow-[0px_4px_20px_rgba(26,31,43,0.06)]">
              <div className="flex flex-col items-center text-center">
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary-50">
                  <Icon name="smartphone" className="text-3xl text-primary-500" label="Check your phone" />
                </div>
                <h2 className="text-xl font-bold text-text-primary">Check Your Phone</h2>
                <p className="mt-2 text-sm text-text-secondary">
                  We&apos;ve sent a verification link to{" "}
                  <span className="font-semibold text-text-primary">{displayPhone}</span>.
                  Tap it to begin identity verification.
                </p>
              </div>
            </div>
          )
        )}

        {/* ---- Success: already verified (fast path) — sign-in link sent ---- */}
        {step.kind === "success" && step.outcome === "complete" && (
          <div className="rounded-2xl bg-white p-6 shadow-[0px_4px_20px_rgba(26,31,43,0.06)]">
            <div className="flex flex-col items-center text-center">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-50">
                <Icon name="smartphone" className="text-3xl text-verified" label="Check your phone" />
              </div>
              <h2 className="text-xl font-bold text-text-primary">Check Your Phone</h2>
              <p className="mt-2 text-sm text-text-secondary">
                You&apos;re already verified! We&apos;ve sent a sign-in link to{" "}
                <span className="font-semibold text-text-primary">{displayPhone}</span>.
              </p>
            </div>
          </div>
        )}

        {/* Sign in prompt */}
        {(step.kind === "form" || step.kind === "waiting") && (
          <p className="mt-6 text-center text-sm text-text-secondary">
            Already verified?{" "}
            <Link
              to="/login"
              className="font-semibold text-primary-500 transition-colors hover:text-primary-600"
            >
              Sign in
            </Link>
          </p>
        )}

        {/* Footer */}
        <div className="mt-6 flex items-center justify-center gap-4 text-xs text-text-muted">
          <Link to="/privacy" className="transition-colors hover:text-text-secondary">
            Privacy Policy
          </Link>
          <span>·</span>
          <Link to="/terms" className="transition-colors hover:text-text-secondary">
            Terms of Service
          </Link>
          <span>·</span>
          <Link to="/support" className="transition-colors hover:text-text-secondary">
            Support
          </Link>
        </div>

      </div>
    </div>
  );
}
